import { Component, inject, Input, OnInit } from '@angular/core';
import { EpTableComponent, EPTableConfig } from '@ep/shared';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { Report } from '@app/resources/services';
import { selectReports } from '@app/resources/ngrx/selectors';
import { ReportActions } from '@app/resources/ngrx/actions';
import { ReportModuleActions } from '@app/areas/reports/store';
import { NgIf, AsyncPipe } from '@angular/common';
import { PortalTheme } from '@app/@shared/models';
import { CssValidatorService } from '@app/@shared/services/css-validator.service';

@Component({
  selector: 'app-reports-table',
  standalone: true,
  imports: [EpTableComponent, NgIf, AsyncPipe],
  templateUrl: './reports-table.component.html',
})
export class ReportsTableComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly cssValidatorService = inject(CssValidatorService);

  @Input() public theme: PortalTheme | null = null;
  protected config!: EPTableConfig<Report>;
  protected reports$: Observable<Report[]> | undefined;

  ngOnInit(): void {
    this.store.dispatch(ReportActions.loadReports());
    this.reports$ = this.store.select(selectReports);
    this.initializeTableConfig();
    this.setCSSCustomProperties();
  }

  private initializeTableConfig(): void {
    this.config = {
      header: 'reports',
      columns: [
        {
          header: 'Name',
          field: 'NamePrivate',
          columnType: 'string',
          defaultValue: 'Report',
        },
        {
          header: 'Description',
          field: 'Display',
          columnType: 'string',
          defaultValue: 'Daily Sales',
        },
      ],
      style: {
        'custom-header-background': this.theme?.table.header.backgroundColor || '',
        'custom-table-subheader-background': this.theme?.table.subHeaderBackgroundColor || '',
        'custom-table-subheader-text-color': this.theme?.table.subHeaderTextColor || '',
        'custom-header-text-color': this.theme?.table.header.textColor || '',
        'custom-table-body-primary-background': this.theme?.table.body.primaryBackgroundColor || '',
        'custom-table-body-secondary-background': this.theme?.table.body.secondaryBackgroundColor || '',
        'custom-table-body-text-color': this.theme?.table.body.textColor || '',
      },
      globalFilterFields: ['Name'],
      actions: {
        rowClick: (report: Report) => {
          this.store.dispatch(
            ReportModuleActions.navigateExistingReport({
              payload: { reportId: report.ReportId },
            })
          );
        },
      },
    };
  }

  private setCSSCustomProperties(): void {
    if (this.theme) {
      // Set CSS custom properties
      document.documentElement.style.setProperty(
        '--custom-header-background',
        this.theme?.table.header.backgroundColor
      );
      document.documentElement.style.setProperty(
        '--custom-table-subheader-background',
        this.theme?.table.subHeaderBackgroundColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-subheader-text-color',
        this.theme?.table.subHeaderTextColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-header-text-color',
        this.theme?.table.header.textColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-body-primary-background',
        this.theme?.table.body.primaryBackgroundColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-body-secondary-background',
        this.theme?.table.body.secondaryBackgroundColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-body-text-color',
        this.theme?.table.body.textColor || ''
      );

      // Check for undefined CSS variables
      this.cssValidatorService.checkUndefinedCssVariables('ReportsTableComponent');
    }
  }
}
